import React, { useEffect, useState } from 'react';
import { Avatar, Badge } from '../../components/ui';
import { channelsApi } from '../../api/client';
import type { Message } from '../../api/types';

interface Props {
  parentMessage: Message;
  onClose: () => void;
}

export const ThreadPanel: React.FC<Props> = ({ parentMessage, onClose }) => {
  const [replies, setReplies] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    setLoading(true);
    channelsApi.messages(parentMessage.channelId).then((res) => {
      if (res.ok) {
        const thread = res.data.filter(
          (m) => m.threadParentId === parentMessage.id,
        );
        setReplies(thread);
      }
      setLoading(false);
    });
  }, [parentMessage.id, parentMessage.channelId]);

  const handleSendReply = async () => {
    if (!reply.trim() || sending) return;
    const content = reply.trim();
    setReply('');
    setSending(true);
    try {
      await channelsApi.sendMessage(parentMessage.channelId, content, {
        threadParentId: parentMessage.id,
      });
      // Re-fetch replies
      const res = await channelsApi.messages(parentMessage.channelId);
      if (res.ok) {
        setReplies(res.data.filter((m) => m.threadParentId === parentMessage.id));
      }
    } finally {
      setSending(false);
    }
  };

  const senderName =
    parentMessage.agent?.displayName ??
    parentMessage.user?.displayName ??
    'Unknown';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div
        style={{
          padding: '12px 16px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexShrink: 0,
        }}
      >
        <span style={{ fontWeight: 600, fontSize: 14, flex: 1 }}>Thread</span>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: 18,
            color: 'var(--text-muted)',
          }}
        >
          ✕
        </button>
      </div>

      <div
        style={{
          flex: 1,
          overflow: 'auto',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        {/* Original message */}
        <div
          style={{
            padding: '10px 12px',
            background: 'var(--bg-secondary)',
            borderRadius: 'var(--radius)',
            borderLeft: '3px solid var(--primary)',
          }}
        >
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
            {senderName} ·{' '}
            {new Date(parentMessage.createdAt).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </div>
          <p style={{ fontSize: 13, lineHeight: 1.5 }}>{parentMessage.content}</p>
        </div>

        {loading && (
          <div style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center' }}>
            Loading replies…
          </div>
        )}

        <div style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 500 }}>
          {replies.length} {replies.length === 1 ? 'reply' : 'replies'}
        </div>

        {replies.map((r) => {
          const rSender =
            r.agent?.displayName ?? r.user?.displayName ?? 'Unknown';
          const rInitials = rSender.slice(0, 2).toUpperCase();
          const isAgent = !!r.agent;
          return (
            <div key={r.id} style={{ display: 'flex', gap: 8 }}>
              <Avatar
                initials={rInitials}
                color={isAgent ? '#4F46E5' : '#7C3AED'}
                size="xs"
              />
              <div>
                <div
                  style={{
                    display: 'flex',
                    gap: 6,
                    alignItems: 'center',
                    marginBottom: 2,
                  }}
                >
                  <span style={{ fontWeight: 600, fontSize: 13 }}>{rSender}</span>
                  {isAgent && <Badge variant="purple">AI</Badge>}
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    {new Date(r.createdAt).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>
                <div style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.5 }}>
                  {r.content}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Reply input */}
      <div
        style={{
          padding: '12px 16px',
          borderTop: '1px solid var(--border)',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            display: 'flex',
            gap: 8,
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            padding: '6px 10px',
            background: 'white',
          }}
        >
          <input
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendReply();
              }
            }}
            placeholder="Reply in thread…"
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              fontSize: 13,
              background: 'transparent',
            }}
          />
          <button
            onClick={handleSendReply}
            disabled={!reply.trim() || sending}
            style={{
              background: 'var(--primary)',
              color: 'white',
              border: 'none',
              borderRadius: 6,
              padding: '4px 10px',
              fontSize: 12,
              fontWeight: 600,
              cursor: reply.trim() && !sending ? 'pointer' : 'not-allowed',
              opacity: reply.trim() && !sending ? 1 : 0.5,
            }}
          >
            Reply
          </button>
        </div>
      </div>
    </div>
  );
};
